import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import API_BASE from '../config';

const ROLES = [
  { value: 'team_member', label: 'Team Member' },
  { value: 'client_admin', label: 'Client Admin' },
  { value: 'client_member', label: 'Client Member' },
];

const CLIENT_ROLES = ['client_admin', 'client_member'];

export default function UserApproval() {
  const { getToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState({});
  const [billingResult, setBillingResult] = useState({});

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/users/pending`).then(r => r.json()),
      fetch(`${API_BASE}/clients`).then(r => r.json()),
    ]).then(([pending, allClients]) => {
      const pendingUsers = Array.isArray(pending) ? pending : [];
      const allC = Array.isArray(allClients) ? allClients : [];
      setUsers(pendingUsers);
      setClients(allC);
      const init = {};
      const initBilling = {};
      pendingUsers.forEach(u => {
        init[u.id] = {
          role: '',
          client_id: u.client_id ? String(u.client_id) : '',
        };
        // Restore sent state from server timestamps so it survives page navigation
        if (u.proposal_sent_at) initBilling[u.id] = true;
        if (u.invoice_sent_at) initBilling[u.id] = true;
      });
      setSelections(init);
      setBillingResult(initBilling);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function setField(userId, field, value) {
    setSelections(prev => ({ ...prev, [userId]: { ...prev[userId], [field]: value } }));
  }

  // Returns 'proposal' for managed_service or missing track, 'invoice' for saas
  function billingMode(user) {
    return user.service_track === 'saas' ? 'invoice' : 'proposal';
  }

  async function handleBilling(user) {
    const sel = selections[user.id] || {};
    const mode = billingMode(user);
    const endpoint = mode === 'invoice' ? '/billing/invoice' : '/billing/proposal';

    setWorking(prev => ({ ...prev, [user.id]: 'billing' }));
    try {
      const token = await getToken();
      console.log('[handleBilling] client_id being sent:', sel.client_id || user.client_id || null);
      console.log('[handleBilling] selections for user:', selections[user.id]);
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          clerkUserId: user.id,
          client_name: user.company_name || user.name || user.email,
          contact_name: user.name || '',
          contact_email: user.email,
          service_track: user.service_track,
          service_tier: user.service_tier,
          client_id: sel.client_id || user.client_id || null,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      const url = data.invoice_url || data.payment_link_url || true;
      setBillingResult(prev => ({ ...prev, [user.id]: url }));
    } catch (err) {
      alert(err.message);
    }
    setWorking(prev => ({ ...prev, [user.id]: null }));
  }

  async function handleApprove(user) {
    const sel = selections[user.id] || {};
    if (!sel.role) return alert('Select a role first');
    if (['client_admin', 'client_member'].includes(sel.role) && !sel.client_id && !user.service_track) return alert('Select a client for this role');

    setWorking(prev => ({ ...prev, [user.id]: 'approving' }));
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/users/${user.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ role: sel.role, client_id: sel.client_id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (err) {
      alert(err.message);
    }
    setWorking(prev => ({ ...prev, [user.id]: null }));
  }

  async function handleReject(user) {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;

    setWorking(prev => ({ ...prev, [user.id]: 'rejecting' }));
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/users/${user.id}/reject`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_id: user.client_id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (err) {
      alert(err.message);
    }
    setWorking(prev => ({ ...prev, [user.id]: null }));
  }

  const trackLabel = t => t === 'managed_service' ? 'Managed Service' : t === 'saas' ? 'SaaS' : t || '—';

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A', margin: '0 0 0.25rem' }}>Pending Users</h1>
      <p style={{ fontSize: '0.875rem', color: '#888', margin: '0 0 2rem' }}>Review new signups, send proposals or invoices, and grant access.</p>

      {loading && <p style={{ fontSize: '0.875rem', color: '#888' }}>Loading…</p>}

      {!loading && users.length === 0 && (
        <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '2rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#888' }}>No pending users.</p>
        </div>
      )}

      {!loading && users.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {users.map((user) => {
            const sel = selections[user.id] || {};
            const busy = working[user.id];
            const sent = billingResult[user.id];
            const hasServiceData = user.service_track && user.service_tier;
            const mode = billingMode(user);
            const canApprove = sent || !hasServiceData;

            return (
              <div key={user.id} style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
                {/* User info row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
                      <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#1A1A1A' }}>{user.name || user.email}</p>
                      {user.company_name && (
                        <span style={{ fontSize: '0.72rem', backgroundColor: '#F5E6E6', color: '#8B1A1A', padding: '0.15rem 0.5rem', borderRadius: '20px', fontWeight: '500' }}>{user.company_name}</span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>{user.email}</p>
                  </div>
                  {user.service_track && (
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ margin: '0 0 0.15rem', fontSize: '0.78rem', color: '#888' }}>{trackLabel(user.service_track)}</p>
                      <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '600', color: '#8B1A1A' }}>{user.price_label || user.service_tier}</p>
                    </div>
                  )}
                </div>

                {/* Controls row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <select
                    value={sel.role || ''}
                    onChange={e => setField(user.id, 'role', e.target.value)}
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem', border: '1px solid #D4CFC9', borderRadius: '5px', color: '#1A1A1A', background: '#fff', cursor: 'pointer' }}
                  >
                    <option value="">Select role…</option>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>

                  <select
                    value={sel.client_id || ''}
                    onChange={e => setField(user.id, 'client_id', e.target.value)}
                    style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem', border: '1px solid #D4CFC9', borderRadius: '5px', color: '#1A1A1A', background: '#fff', cursor: 'pointer' }}
                  >
                    <option value="">Select client…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>

                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
                    {hasServiceData && (
                      sent ? (
                        <span style={{ fontSize: '0.8rem', color: '#276749', fontWeight: '500' }}>
                          ✓ {mode === 'invoice' ? 'Invoice' : 'Proposal'} sent
                          {typeof sent === 'string' && (
                            <a href={sent} target="_blank" rel="noreferrer" style={{ marginLeft: '0.5rem', color: '#1A56A0', textDecoration: 'none' }}>View →</a>
                          )}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleBilling(user)}
                          disabled={!!busy}
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.9rem', backgroundColor: '#1A56A0', color: '#fff', border: 'none', borderRadius: '5px', cursor: busy ? 'default' : 'pointer', opacity: busy === 'billing' ? 0.6 : 1 }}
                        >
                          {busy === 'billing' ? 'Sending…' : mode === 'invoice' ? 'Send Invoice' : 'Send Proposal'}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => handleApprove(user)}
                      disabled={!!busy || !canApprove}
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.9rem', backgroundColor: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '5px', cursor: (busy || !canApprove) ? 'not-allowed' : 'pointer', opacity: (busy || !canApprove) ? 0.5 : 1 }}
                    >
                      {busy === 'approving' ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleReject(user)}
                      disabled={!!busy}
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.9rem', backgroundColor: 'transparent', color: '#888', border: '1px solid #D4CFC9', borderRadius: '5px', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                    >
                      {busy === 'rejecting' ? 'Rejecting…' : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
